import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, MagnifyingGlass, Spinner, ArrowClockwise, Flame, Eye, CursorClick,
  EnvelopeSimple, Warning, CheckCircle, XCircle, Clock, ArrowUpRight,
} from "@phosphor-icons/react";
import axios from "axios";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtRelative(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts.endsWith?.("Z") || ts.includes?.("+") ? ts : ts + "Z");
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 5) return "just now";
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch {
    return ts;
  }
}

function fmtAbsolute(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts.endsWith?.("Z") || ts.includes?.("+") ? ts : ts + "Z");
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(d);
  } catch {
    return "";
  }
}

const STATUS_PILL = {
  opened:      { label: "OPENED",     cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  delivered:   { label: "DELIVERED",  cls: "bg-blue-50 text-blue-700 border-blue-200" },
  pending:     { label: "PENDING",    cls: "bg-slate-50 text-slate-600 border-slate-200" },
  mocked:      { label: "MOCK",       cls: "bg-slate-50 text-slate-500 border-slate-200" },
  bounced:     { label: "BOUNCED",    cls: "bg-red-50 text-red-700 border-red-300" },
  spam:        { label: "SPAM",       cls: "bg-red-50 text-red-700 border-red-300" },
  auth_failed: { label: "AUTH FAIL",  cls: "bg-amber-50 text-amber-700 border-amber-300" },
  error:       { label: "ERROR",      cls: "bg-amber-50 text-amber-700 border-amber-300" },
};

function StatusPill({ status }) {
  const s = STATUS_PILL[status] || { label: (status || "—").toUpperCase(), cls: "bg-slate-50 text-slate-500 border-slate-200" };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-wider border ${s.cls}`} data-testid={`hot-leads-status-${status}`}>
      {s.label}
    </span>
  );
}

function HeatBadge({ score, last_open_at }) {
  // Animated flame for genuinely live leads (real open in last 60 min).
  const isHot = (() => {
    if (!last_open_at) return false;
    try {
      const d = new Date(last_open_at.endsWith?.("Z") || last_open_at.includes?.("+") ? last_open_at : last_open_at + "Z");
      return (Date.now() - d.getTime()) < 60 * 60 * 1000;
    } catch { return false; }
  })();
  const v = Math.max(0, Math.min(100, Math.round(score || 0)));
  const tone = isHot ? "text-orange-600 bg-orange-50 border-orange-300"
              : v > 40 ? "text-blue-700 bg-blue-50 border-blue-200"
              : v > 0  ? "text-slate-600 bg-slate-50 border-slate-200"
              : "text-slate-400 bg-slate-50 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-bold ${tone}`} title={`Heat score ${v}`}>
      <Flame size={11} weight={isHot ? "fill" : "bold"} className={isHot ? "animate-pulse" : ""} />
      {v}
    </span>
  );
}

function MetricCard({ label, value, color = "slate", testId, icon: Icon, sub }) {
  const tone = {
    orange: "text-orange-600 bg-orange-50/60",
    blue:   "text-blue-700 bg-blue-50/60",
    emerald:"text-emerald-700 bg-emerald-50/60",
    red:    "text-red-700 bg-red-50/60",
    amber:  "text-amber-700 bg-amber-50/60",
    slate:  "text-slate-700 bg-slate-50/60",
  }[color];
  return (
    <div className={`flex-1 min-w-[140px] rounded-md border border-slate-200 p-3 ${tone}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider opacity-80">
        {Icon && <Icon size={12} weight="bold" />}
        {label}
      </div>
      <div className="text-2xl font-black mt-1 leading-none font-['Chivo']">{value}</div>
      {sub && <div className="text-[10px] mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

export default function HotLeadsPage({ user }) {
  const tx = useT();
  const [data, setData] = useState({ summary: null, rows: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hours, setHours] = useState(24);
  const [status, setStatus] = useState("any");
  const [search, setSearch] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [tick, setTick] = useState(0); // re-render relative timestamps
  const [searchParams] = useSearchParams();
  const focusSendId = searchParams.get("send") || "";
  // Whether the focus row has been auto-scrolled-to once (don't yank
  // the user's view on every auto-refresh).
  const focusedOnce = useRef(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await axios.get(`${API}/admin/hot-leads`, {
        params: {
          requester_email: user?.email || "",
          hours, status, search: search.trim(), limit: 200,
        },
        withCredentials: true,
        headers: user?.email ? { "X-User-Email": user.email } : {},
      });
      setData(res.data || { summary: null, rows: [], count: 0 });
      setLastFetched(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [user?.email, hours, status, search]);

  useEffect(() => { load(); }, [load]);

  // When arriving from a Hot-Lead email deep-link, broaden the window once
  // so the targeted row is guaranteed to be in the result set.
  useEffect(() => {
    if (focusSendId && hours < 168) setHours(168);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(() => load(), 20000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  // Tick every 15s so "5m ago" timestamps stay fresh between hard reloads.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 15000);
    return () => clearInterval(t);
  }, []);

  // Auto-scroll to + highlight the focused send_id (from email deep-link).
  useEffect(() => {
    if (!focusSendId || focusedOnce.current || !data.rows?.length) return;
    const el = document.querySelector(`[data-testid="hot-leads-row-${focusSendId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-orange-400", "bg-orange-100/60");
      focusedOnce.current = true;
      setTimeout(() => el.classList.remove("ring-2", "ring-orange-400", "bg-orange-100/60"), 4000);
    }
  }, [data.rows, focusSendId]);

  const summary = data.summary || {};
  const rows = data.rows || [];

  const hotCount = useMemo(
    () => rows.filter((r) => {
      if (!r.last_open_at) return false;
      try {
        const d = new Date(r.last_open_at.endsWith?.("Z") || r.last_open_at.includes?.("+") ? r.last_open_at : r.last_open_at + "Z");
        return Date.now() - d.getTime() < 60 * 60 * 1000;
      } catch { return false; }
    }).length,
    [rows, tick] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-slate-400 hover:text-slate-700" data-testid="hot-leads-back">
              <ArrowLeft size={18} />
            </Link>
            <div className="flex items-center gap-2">
              <Flame size={18} weight="fill" className="text-orange-500" />
              <h1 className="text-lg font-black tracking-tighter font-['Chivo']">{tx("hot_leads.title", "Hot Leads")}</h1>
              {hotCount > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-orange-50 border border-orange-300 text-[10px] font-bold text-orange-700 animate-pulse" data-testid="hot-leads-live-count">
                  {hotCount} {tx("hot_leads.live_now", "live now")}
                </span>
              )}
            </div>
            <span className="text-xs text-slate-400 hidden md:inline">
              {lastFetched ? `Updated ${fmtRelative(lastFetched.toISOString())}` : ""}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-[11px] text-slate-600 select-none cursor-pointer" data-testid="hot-leads-autorefresh-label">
              <input
                type="checkbox" checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                data-testid="hot-leads-autorefresh-toggle"
              />
              Auto-refresh
            </label>
            <button
              onClick={load}
              className="h-8 px-2.5 flex items-center gap-1 rounded-sm border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-600"
              data-testid="hot-leads-refresh-btn"
            >
              <ArrowClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6 space-y-5">
        {/* Summary metric strip */}
        <section className="flex flex-wrap gap-3" data-testid="hot-leads-metrics">
          <MetricCard label={tx("hot_leads.hot_now", "Hot now")} value={summary.hot_now ?? "—"} color="orange" icon={Flame} sub={tx("hot_leads.real_opens_60min", "real opens in last 60 min")} testId="metric-hot-now" />
          <MetricCard label="Opened" value={summary.opened ?? "—"} color="emerald" icon={Eye} sub={`${Math.round((summary.open_rate || 0) * 100)}% open rate`} testId="metric-opened" />
          <MetricCard label="Clicked" value={summary.clicked ?? "—"} color="blue" icon={CursorClick} testId="metric-clicked" />
          <MetricCard label="Sent" value={summary.total_sent ?? "—"} color="slate" icon={EnvelopeSimple} sub={`${summary.delivered ?? 0} delivered`} testId="metric-sent" />
          <MetricCard label="Bounced" value={summary.bounced ?? "—"} color="red" icon={Warning} testId="metric-bounced" />
        </section>

        {/* Filter bar */}
        <section className="flex flex-wrap items-center gap-2 p-2 rounded-md border border-slate-200 bg-slate-50/50">
          <div className="flex items-center gap-1 bg-white rounded-sm border border-slate-200 px-2 h-8 flex-1 min-w-[200px]">
            <MagnifyingGlass size={13} className="text-slate-400" />
            <input
              type="text" placeholder={tx("hot_leads.search_placeholder", "Search email, quote ID, subject…")}
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent outline-none text-xs w-full"
              data-testid="hot-leads-search"
            />
          </div>
          <select
            value={hours} onChange={(e) => setHours(Number(e.target.value))}
            className="h-8 px-2 rounded-sm border border-slate-200 bg-white text-xs"
            data-testid="hot-leads-hours-select"
          >
            <option value={1}>{tx("hot_leads.last_1_hour", "Last 1 hour")}</option>
            <option value={6}>{tx("hot_leads.last_6_hours", "Last 6 hours")}</option>
            <option value={24}>{tx("hot_leads.last_24_hours", "Last 24 hours")}</option>
            <option value={72}>{tx("hot_leads.last_3_days", "Last 3 days")}</option>
            <option value={168}>{tx("hot_leads.last_7_days", "Last 7 days")}</option>
            <option value={720}>{tx("hot_leads.last_30_days", "Last 30 days")}</option>
          </select>
          <select
            value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-8 px-2 rounded-sm border border-slate-200 bg-white text-xs"
            data-testid="hot-leads-status-select"
          >
            <option value="any">{tx("hot_leads.all_statuses", "All statuses")}</option>
            <option value="hot">🔥 {tx("hot_leads.hot_live_opens", "Hot (live opens)")}</option>
            <option value="clicked">Clicked</option>
            <option value="opened">Opened</option>
            <option value="delivered">Delivered</option>
            <option value="bounced">{tx("hot_leads.bounced_spam", "Bounced / Spam")}</option>
            <option value="pending">{tx("hot_leads.pending_failed", "Pending / Failed")}</option>
          </select>
        </section>

        {/* Table */}
        <section className="rounded-md border border-slate-200 overflow-hidden">
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center p-12 text-slate-500">
              <Spinner size={20} className="animate-spin mr-2" />
              Loading…
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-red-700 bg-red-50 border-b border-red-200" data-testid="hot-leads-error">
              <Warning size={14} className="inline -mt-0.5 mr-1" /> {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-400" data-testid="hot-leads-empty">
              {tx("hot_leads.empty", "No emails match the current filters.")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="hot-leads-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2 w-[60px]">{tx("table.heat", "Heat")}</th>
                    <th className="px-3 py-2">{tx("table.recipient", "Recipient")}</th>
                    <th className="px-3 py-2">{tx("table.quote", "Quote")}</th>
                    <th className="px-3 py-2">{tx("table.subject", "Subject")}</th>
                    <th className="px-3 py-2 w-[100px]">{tx("table.status", "Status")}</th>
                    <th className="px-3 py-2 text-right w-[80px]">{tx("table.opens", "Opens")}</th>
                    <th className="px-3 py-2 text-right w-[80px]">{tx("table.clicks", "Clicks")}</th>
                    <th className="px-3 py-2 w-[140px]">{tx("hot_leads.last_activity", "Last activity")}</th>
                    <th className="px-3 py-2 w-[140px]">{tx("table.sent", "Sent")}</th>
                    <th className="px-3 py-2 w-[60px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isHotRow = (() => {
                      if (!r.last_open_at) return false;
                      try {
                        const d = new Date(r.last_open_at.endsWith?.("Z") || r.last_open_at.includes?.("+") ? r.last_open_at : r.last_open_at + "Z");
                        return Date.now() - d.getTime() < 60 * 60 * 1000;
                      } catch { return false; }
                    })();
                    return (
                      <tr
                        key={r.send_id}
                        className={`border-b border-slate-100 hover:bg-blue-50/40 ${isHotRow ? "bg-orange-50/40" : ""}`}
                        data-testid={`hot-leads-row-${r.send_id}`}
                        data-hot={isHotRow ? "1" : "0"}
                      >
                        <td className="px-3 py-2">
                          <HeatBadge score={r.heat_score} last_open_at={r.last_open_at} />
                        </td>
                        <td className="px-3 py-2">
                          <a href={`mailto:${r.to_email}`} className="text-blue-700 hover:underline font-medium break-all">
                            {r.to_email || "—"}
                          </a>
                          {r.company_name && (
                            <div className="text-[10px] text-slate-500 truncate max-w-[260px]" title={r.company_name}>
                              {r.company_name}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {r.quote_id ? (
                            <Link
                              to={`/quote/${encodeURIComponent(r.quote_id)}`}
                              className="text-blue-700 hover:underline font-mono text-[11px]"
                              data-testid={`hot-leads-quote-link-${r.quote_id}`}
                            >
                              {r.quote_id}
                            </Link>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                          {r.brand && (
                            <span className="ml-1.5 px-1 py-0.5 text-[9px] font-bold rounded-sm bg-slate-100 text-slate-600 border border-slate-200">
                              {r.brand}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-700 max-w-[280px] truncate" title={r.subject}>
                          {r.subject || "—"}
                          {r.actor_email && (
                            <div className="text-[10px] text-slate-400 truncate">by {r.actor_email}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <StatusPill status={r.status} />
                          {r.bounced?.reason && (
                            <div className="text-[9px] text-red-600 mt-0.5 truncate max-w-[100px]" title={r.bounced.reason}>
                              {r.bounced.reason}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-bold ${r.opens_count > 0 ? "text-emerald-700" : "text-slate-300"}`}>
                            {r.opens_count}
                          </span>
                          {r.opens_proxy_count > 0 && (
                            <span className="text-[9px] text-slate-400 ml-1" title={tx("hot_leads.proxy_opens_tip", "Proxy / prefetch opens (Gmail image scanner)")}>
                              +{r.opens_proxy_count}p
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-bold ${r.clicks_count > 0 ? "text-blue-700" : "text-slate-300"}`}>
                            {r.clicks_count}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {r.last_open_at ? (
                            <div title={fmtAbsolute(r.last_open_at)}>
                              <Eye size={11} className="inline -mt-0.5 mr-1 text-emerald-600" />
                              {fmtRelative(r.last_open_at)}
                            </div>
                          ) : r.last_event_at ? (
                            <div title={fmtAbsolute(r.last_event_at)} className="text-slate-500">
                              <Clock size={11} className="inline -mt-0.5 mr-1" />
                              {fmtRelative(r.last_event_at)}
                            </div>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-500" title={fmtAbsolute(r.created_at)}>
                          {fmtRelative(r.created_at)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {r.quote_id && (
                            <Link
                              to={`/quote/${encodeURIComponent(r.quote_id)}`}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-sm text-slate-400 hover:bg-blue-50 hover:text-blue-700"
                              title={tx("hot_leads.open_quote", "Open quote")}
                            >
                              <ArrowUpRight size={13} weight="bold" />
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="text-[10px] text-slate-400">
          {tx("hot_leads.heat_explanation", "Heat score combines real opens, clicks, and recency. Gmail's image proxy hits are counted separately (+Np) so automated prefetches don't fake \"live\" activity.")}
        </p>
      </main>
    </div>
  );
}
